import { useRef, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuditlogData } from "./data/ServiceApplicationDetails.data";
import { AuditlogConfig } from "./data/AuditlogConfig";
import FormCreator from "../../../components/Forms/FormCreator";
import Buttons from "../../../components/ui/button/Buttons";
import Accordion from "../../../components/Accordion/Accordion";
import CenterModel from "../../../components/ui/centerModel/CenterModel";
import MolGrid from "../../../components/Grid/MolGrid";
import CompanyInformation from "./features/companyInformation/CompanyInformation";
import SelfEvaluationInfo from "./features/SelfEvaluationInformation/SelfEvaluationInfo";
import CertificateGrouping from "./features/CertificateGrouping/CertificateGrouping";
import { decryptAESID } from "../../../utils/Encryption";
// TODO: Service mate separate hoy to hook na naam badlo
import {
  useGetSGBPSApplicationAuditLogListMutation,
  useLazyGetUpdatedByDropdownForAuditLogQuery,
} from "../../../app/services/CertificateGrouping/certificateGroupingApi";

const pad = (n) => String(n).padStart(2, "0");

const formatDateOnly = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const initialPagination = {
  pageNo: 1,
  pageSize: 10,
  SearchText: null,
  DateFrom: null,
  DateTo: null,
  UpdatedBy: null,
  totalCount: 0,
};

const ServiceApplicationDetails = () => {
  const AuditlogRef = useRef();
  const molGridRef = useRef();
  const navigate = useNavigate();
  const { id, id2, tempId } = useParams();

  const templateID = tempId ? decryptAESID(tempId, false) : 0;
  const productInfoID = id ? decryptAESID(id, false) : 0;
  const applicationID = id2 ? decryptAESID(id2, false) : 0;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [formData, setFormData] = useState(AuditlogData);
  const [formKey, setFormKey] = useState(0); // Clear par form remount mate
  const [paginationRequest, setPaginationRequest] = useState({
    ...initialPagination,
    SGBPSApplicationId: Number(applicationID),
  });

  const [
    getAuditLogList,
    {
      isLoading: auditLogLoading,
      isSuccess: auditLogSuccess,
      data: auditLogData,
    },
  ] = useGetSGBPSApplicationAuditLogListMutation();

  const [
    getUpdatedByDropdown,
    { isLoading: updatedByLoading, data: updatedByData },
  ] = useLazyGetUpdatedByDropdownForAuditLogQuery();

  /* ---------- Dropdown ---------- */
  const setAllDropDownField = (apiResponse) => {
    const updatedByList = (apiResponse || []).map((item) => ({
      value: item.id,
      label: item.name,
    }));
    const dropdownLists = { updatedBy: updatedByList };

    setFormData((prev) => ({
      ...prev,
      formFields: prev.formFields.map((field) => {
        const options = dropdownLists[field?.id];
        return options
          ? { ...field, fieldSetting: { ...field.fieldSetting, options } }
          : field;
      }),
    }));
  };

  useEffect(() => {
    if (!updatedByLoading && updatedByData) {
      setAllDropDownField(updatedByData);
    }
  }, [updatedByLoading, updatedByData]);

  /* ---------- Modal open / close ---------- */
  const openModal = () => {
    setIsModalOpen(true);
    if (applicationID > 0) {
      getUpdatedByDropdown(applicationID);
      getAuditLogList({
        ...initialPagination,
        SGBPSApplicationId: Number(productInfoID),
      });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setDataSource([]);
    setFormData({ ...AuditlogData });
    setFormKey((k) => k + 1);
    if (updatedByData) setAllDropDownField(updatedByData);
    setPaginationRequest({
      ...initialPagination,
      SGBPSApplicationId: Number(productInfoID),
    });
  };

  /* ---------- Search / Clear / Pagination ---------- */
  const handleSearch = () => {
    const auditFormData = AuditlogRef?.current?.getFormData();
    const from = formatDateOnly(auditFormData?.fromDate);
    const to = formatDateOnly(auditFormData?.toDate);

    if (from && to && from > to) {
      // TODO: tamara toast/alert util thi message batavo
      alert("From Date, To Date karta moti na hovi joiye.");
      return;
    }

    const request = {
      ...paginationRequest,
      pageNo: 1,
      pageSize: 10,
      SGBPSApplicationId: Number(productInfoID),
      SearchText: auditFormData?.searchByDescription || null,
      DateFrom: from,
      DateTo: to,
      UpdatedBy: auditFormData?.updatedBy?.label || null, // backend id mange to .value
    };
    setPaginationRequest(request);
    getAuditLogList(request);
  };

  const handleClearClick = () => {
    const request = {
      ...paginationRequest,
      pageNo: 1,
      pageSize: 10,
      SGBPSApplicationId: Number(productInfoID),
      SearchText: null,
      DateFrom: null,
      DateTo: null,
      UpdatedBy: null,
    };
    setPaginationRequest(request);
    getAuditLogList(request);
    setFormKey((k) => k + 1); // form fresh mount => fields reset
  };

  const handlePageChange = ({ pageNumber, pageSize }) => {
    const request = { ...paginationRequest, pageNo: pageNumber, pageSize };
    setPaginationRequest(request);
    getAuditLogList(request);
  };

  /* ---------- API response -> grid ---------- */
  useEffect(() => {
    if (!auditLogLoading && auditLogSuccess && auditLogData) {
      setPaginationRequest((prev) => ({
        ...prev,
        totalCount: auditLogData?.totalCount,
      }));
      setDataSource(
        auditLogData?.itemList?.map((item) => ({
          ...item,
          DateTime: formatDateTime(item?.auditDate),
          Description: item?.description,
          UpdatedBy: item?.updatedBy,
        })) || []
      );
    }
  }, [auditLogLoading, auditLogSuccess, auditLogData]);

  return (
    <div className="service-application-details-page">
      <div className="d-flex gap-3 mb-4 justify-content-end">
        <Buttons
          buttonText="Audit Log"
          buttonTypeClassName="success-btn"
          onClick={openModal}
        />
      </div>

      <CompanyInformation />

      <Accordion title="Self Evaluation Information" className="mb-4" button={false} accordion={true}>
        <SelfEvaluationInfo />
      </Accordion>

      <Accordion title="Certificate Grouping" className="mb-4" button={false} accordion={true}>
        <CertificateGrouping productInfoID={productInfoID} typeId={templateID} />
      </Accordion>

      <div className="d-flex justify-content-between mt-4">
        <Buttons
          buttonText="Back"
          buttonTypeClassName="black-btn"
          onClick={() => navigate("/certificateCorporateListPage")}
        />
      </div>

      {isModalOpen && (
        <CenterModel modelTitle="" show={isModalOpen} handleClose={closeModal}>
          <div className="crud-requirement-list">
            <div className="corporate-member-list">
              <div className="sgbp-category">
                <div className="row w-100">
                  <div className="col-10 row">
                    {updatedByData && (
                      <FormCreator key={formKey} ref={AuditlogRef} {...formData} />
                    )}
                  </div>
                  <div className="col-2 audit-btn-group">
                    <Buttons buttonText="Search" buttonTypeClassName="search-btn " onClick={handleSearch} />
                    <Buttons buttonText="Clear" buttonTypeClassName="btn btn-secondary" onClick={handleClearClick} />
                  </div>
                </div>
              </div>

              <div className="audit-log-list model-audit-log-list">
                <MolGrid
                  ref={molGridRef}
                  configuration={AuditlogConfig}
                  dataSource={dataSource}
                  isLoading={auditLogLoading}
                  allowPagination={true}
                  pagination={paginationRequest}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </CenterModel>
      )}
    </div>
  );
};

export default ServiceApplicationDetails;